import { AttributeName } from '../../components/AttributeName'
import { Box, MenuItem, Select, Typography } from '@mui/material'
import { Check, TriangleAlert, Minus } from 'lucide-react'
import type { FieldMatch } from '../../api/types'
import { Surface } from '../../components/primitives'
import { tokens } from '../../theme'

interface Props {
  matches: FieldMatch[]
  columns: string[]
  onChange: (field: string, column: string | null) => void
  disabled?: boolean
  /**
   * Fields a run can start without, and what happens if they stay empty —
   * e.g. CO "From the region". Shown plainly rather than in warning colour.
   */
  optional?: Record<string, string>
  /** Which file this mapping is for, when there are several. */
  fileName?: string
}

/**
 * 1a step 2 — the eight EUDR fields against the file's own columns. Some may
 * stay empty (`optional`): CO comes from the region, SU and Certificate may
 * simply not be in the file.
 * Anything the fuzzy match could not place is shown in warning colour, and
 * "Run validation" stays disabled until every row is filled.
 */
export function FieldMapping({ matches, columns, onChange, disabled, optional = {}, fileName }: Props) {
  const mapped = matches.filter((m) => m.column).length

  return (
    <Surface sx={{ p: 5, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 2.5 }}>
        <Typography variant="h4">
          Required fields
          {fileName && (
            <Typography component="span" sx={{ ml: 2, font: `400 12px ${tokens.font.mono}`, color: 'text.secondary' }}>
              {fileName}
            </Typography>
          )}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
          {mapped} of {matches.length} matched
        </Typography>
      </Box>

      <Box
        sx={{
          // Cells stretch to the row so the hairline lands at one height across
          // all four columns; each cell centres its own content instead.
          display: 'grid', gridTemplateColumns: '1fr 20px 1fr 20px', alignItems: 'stretch',
          font: `400 13px ${tokens.font.sans}`, overflowY: 'auto',
        }}
      >
        <Typography variant="overline" sx={{ color: 'text.disabled', py: 1.5 }}>Field</Typography>
        <Box />
        <Typography variant="overline" sx={{ color: 'text.disabled', py: 1.5 }}>Your column</Typography>
        <Box />

        {matches.map((m) => {
          const ok = Boolean(m.column)
          const fallback = ok ? null : (optional[m.field] ?? null)
          const cell = {
            borderTop: `1px solid ${tokens.color.ink200}`,
            display: 'flex',
            alignItems: 'center',
            minHeight: 40,
          }
          const taken = new Set(matches.filter((x) => x.field !== m.field && x.column).map((x) => x.column))
          return (
            <Box key={m.field} sx={{ display: 'contents' }}>
              <AttributeName field={m.field} sx={{ ...cell, fontSize: 12 }} />
              <Box sx={{ ...cell, color: 'text.disabled' }}>→</Box>
              <Box sx={{ ...cell }}>
                <Select
                  value={m.column ?? ''}
                  displayEmpty
                  disabled={disabled}
                  onChange={(e) => onChange(m.field, e.target.value || null)}
                  variant={ok || fallback ? 'standard' : 'outlined'}
                  inputProps={{ 'aria-label': `Column for ${m.field}` }}
                  sx={
                    ok || fallback
                      ? {
                          width: '100%', fontFamily: tokens.font.mono, fontSize: 12, color: 'text.secondary',
                          '& .MuiSelect-select': { p: 0, pr: '20px !important' },
                          '&::before, &::after': { display: 'none' },
                        }
                      : {
                          width: '100%', height: 28, fontSize: 12, color: 'text.secondary',
                          '& fieldset': { borderColor: tokens.color.warning },
                          '& .MuiSelect-select': { py: 0 },
                        }
                  }
                  renderValue={(v) =>
                    v ? String(v)
                      : fallback ? <Box sx={{ color: 'text.disabled', fontFamily: tokens.font.sans }}>{fallback}</Box>
                      : <Box sx={{ color: 'text.secondary' }}>Choose column</Box>
                  }
                >
                  <MenuItem value="">
                    <Box sx={{ color: 'text.disabled' }}>Not in this file</Box>
                  </MenuItem>
                  {columns.map((c) => (
                    <MenuItem key={c} value={c} disabled={taken.has(c)} sx={{ fontFamily: tokens.font.mono }}>
                      {c}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
              <Box sx={{ ...cell, color: ok ? tokens.color.success : fallback ? tokens.color.ink300 : tokens.color.warning }}>
                {ok ? <Check size={14} strokeWidth={2} /> : fallback ? <Minus size={14} strokeWidth={1.5} /> : <TriangleAlert size={14} strokeWidth={1.5} />}
              </Box>
            </Box>
          )
        })}
      </Box>
    </Surface>
  )
}
